import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { getSellers } from '@savgroup-front-common/core/domains/sellers/actionCreators';
import { PRICING_PLAN } from '@savgroup-front-common/types';
import { PRICING_PLAN_STATE } from '@savgroup-front-common/types/Seller';
import { SellerService } from '@/api/Seller/SellerService';
import { BASES } from '@/constants/routing';

import { useAppContext } from '../../../../app/App.context';

import messages from './messages';

const SET_PRICING_PLAN = 'SET_PRICING_PLAN';
const SET_PRICING_PLAN_FREE = 'SET_PRICING_PLAN_FREE';

const usePlanPreview = (isFromScratch: boolean) => {
  const { selectedSeller } = useAppContext();
  const sellerId = selectedSeller?.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    mutateAsync: handleActivateFreePricingPlanMutation,
    isLoading: isActivateFreePricingPlanLoading,
  } = useMutation([SET_PRICING_PLAN_FREE], async () => {
    if (!sellerId) {
      return undefined;
    }

    const response = await SellerService.activateFreePricingPlanCommand({
      sellerId,
    });

    if (response.failure) {
      response.errors.map((error) => {
        toast.error(error.message);
      });

      return undefined;
    }
    toast.success(messages.activateFreePricingPlanSuccess);

    dispatch(getSellers({ forceRefresh: true }));

    return true;
  });

  const {
    mutateAsync: handleActivateAdvancedPricingPlanMutation,
    isLoading: isActivateAdvancedPricingPlanLoading,
  } = useMutation([SET_PRICING_PLAN_FREE], async () => {
    if (!sellerId) {
      return undefined;
    }

    const response = await SellerService.activateAdvancedPricingPlanCommand({
      sellerId,
    });

    if (response.failure) {
      response.errors.map((error) => {
        toast.error(error.message);
      });

      return undefined;
    }
    toast.success(messages.activateFreePricingPlanSuccess);

    dispatch(getSellers({ forceRefresh: true }));

    return true;
  });

  const { mutateAsync: handleAddContract, isLoading: isAddContractLoading } =
    useMutation([SET_PRICING_PLAN], async (plan: PRICING_PLAN) => {
      if (!sellerId || !plan) {
        return undefined;
      }

      const response = await SellerService.setSellerPricingPlanCommand({
        sellerId,
        pricingPlan: plan,
      });

      if (response.failure) {
        toast.error(messages.setPlanError);

        return undefined;
      }
      toast.success(messages.setPlanSuccess);

      dispatch(getSellers({ forceRefresh: true }));

      return undefined;
    });

  const handleSetPlan = async (plan: PRICING_PLAN) => {
    await handleAddContract(plan);
  };

  const handleActivatePricingPlan = async (plan: PRICING_PLAN) => {
    if (plan === PRICING_PLAN.ADVANCED) {
      await handleActivateAdvancedPricingPlanMutation().then(() => {
        if (
          isFromScratch &&
          selectedSeller?.pricingPlanState === PRICING_PLAN_STATE.ACTIVE
        ) {
          navigate(BASES.FILES);
        }
      });
    }

    if (plan === PRICING_PLAN.FREEMIUM) {
      await handleActivateFreePricingPlanMutation().then(() => {
        if (
          isFromScratch &&
          selectedSeller?.pricingPlanState === PRICING_PLAN_STATE.ACTIVE
        ) {
          navigate(BASES.FILES);
        }
      });
    }
  };

  return {
    selectedSeller,
    handleSetPlan,
    isAddContractLoading,
    isActivateLoading:
      isActivateFreePricingPlanLoading || isActivateAdvancedPricingPlanLoading,
    handleActivatePricingPlan,
  };
};

export default usePlanPreview;
